/**
 * Campaign + push-token request/response validators (plan 10 §3.2/§3.3,
 * BP-05 / MB-04). The single validator set reused by the `campaigns` and `me`
 * Edge Functions and the @gym/api-client typed callers.
 */
import { z } from 'zod';

/** Notification channels a campaign may target (mirrors notification_channel). */
export const campaignChannelSchema = z.enum(['push', 'sms', 'email']);

/** The segment a campaign run targets. `custom` is reserved for later. */
export const campaignSegmentSchema = z.enum([
  'soonest_to_pay',
  'expiry_sequence',
  'booked_class',
  'broadcast',
  'custom',
]);
export type CampaignSegment = z.infer<typeof campaignSegmentSchema>;

/** Segment tuning parameters (context-sensitive on the chosen segment). */
export const campaignSegmentParamsSchema = z
  .object({
    /** soonest_to_pay: members whose end_date is within N days. */
    days_window: z.number().int().min(0).max(365).optional(),
    /** expiry_sequence: distinct days-before buckets (e.g. [7,3,1,0]). */
    days_before: z.array(z.number().int().min(0).max(365)).min(1).max(8).optional(),
    /** booked_class: alert members this many hours before their class. */
    hours_before: z.number().min(0).max(168).optional(),
    /** broadcast/promotion: arbitrary template vars (no PII enforced here). */
    vars: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  })
  .default({});

/** POST /api/v1/campaigns/reminders body (plan §3.2). */
export const CampaignTriggerRequestSchema = z.object({
  segment: campaignSegmentSchema,
  channels: z.array(campaignChannelSchema).min(1),
  template_key: z.string().min(1),
  /** ISO-8601; null = send immediately, future = schedule. */
  scheduled_at: z.string().datetime({ offset: true }).nullable().optional(),
  segment_params: campaignSegmentParamsSchema.optional(),
  name: z.string().min(1).max(200),
  /** Dry-run: compute segment_count without sending (CreateDrawer "preview"). */
  dry_run: z.boolean().optional(),
});
export type CampaignTriggerRequest = z.infer<typeof CampaignTriggerRequestSchema>;

/** Response 200 (plan §3.2) — no PII in the body. */
export const CampaignTriggerResponseSchema = z.object({
  campaign_id: z.string().uuid(),
  status: z.enum(['running', 'scheduled', 'completed', 'preview']),
  enqueued_count: z.number().int().nonnegative(),
  segment_count: z.number().int().nonnegative(),
  suppressed_count: z.number().int().nonnegative(),
});
export type CampaignTriggerResponse = z.infer<typeof CampaignTriggerResponseSchema>;

/** POST /api/v1/me/push-token body (plan §3.3). */
export const PushTokenUpsertRequestSchema = z.object({
  token: z.string().min(1).max(512),
  platform: z.enum(['ios', 'android']),
});
export type PushTokenUpsertRequest = z.infer<typeof PushTokenUpsertRequestSchema>;

/** Response 200: the notification_tokens row id. */
export const PushTokenUpsertResponseSchema = z.object({
  id: z.string().uuid(),
});
export type PushTokenUpsertResponse = z.infer<typeof PushTokenUpsertResponseSchema>;
